import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getExpandedRowModel,
  type ColumnDef,
  type ColumnFiltersState,
  type SortingState,
  type VisibilityState,
  type ExpandedState,
  type PaginationState,
  type RowSelectionState,
} from '@tanstack/react-table';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import { PANEL } from '@/lib/surface';
import { EmptyState } from '@/components/common/EmptyState';
import { LoadingState } from '@/components/common/LoadingState';
import { DataTableContent } from './DataTableContent';
import { getSelectionColumn, getExpandColumn } from './systemColumns';
import { DataTableToolbar } from './DataTableToolbar';
import { DataTablePagination } from './DataTablePagination';
import { type DataTableProps, isLegacyColumns } from './types';
import { adaptLegacyColumns } from './adaptColumns';

export function DataTable<T extends object>({
  columns: rawColumns,
  data,
  loading = false,
  // Features
  searchable = false,
  searchPlaceholder,
  toolbar,
  pagination = true,
  pageSize = 10,
  pageSizeOptions,
  exportable = false,
  exportFilename,
  columnToggle = false,
  resizable = false,
  stickyHeader = false,
  maxHeight,
  // Selection
  selectable = false,
  selectedIds,
  onSelectionChange,
  getRowId,
  // Row interaction
  onRowClick,
  rowClassName,
  renderExpandedRow,
  // Empty/Loading states
  emptyTitle,
  emptyDescription,
  emptyAction,
  // Styling
  className,
  caption,
  // Filters
  facetedFilters,
  // Server-side pagination
  manualPagination = false,
  pageCount,
  onPaginationChange,
}: DataTableProps<T>) {
  // Adapt legacy columns if needed
  const adaptedColumns = isLegacyColumns(rawColumns) ? adaptLegacyColumns(rawColumns) : rawColumns;

  // Build final columns array with optional select/expand columns
  const finalColumns: ColumnDef<T, unknown>[] = [];
  if (renderExpandedRow) {
    finalColumns.push(getExpandColumn<T>() as ColumnDef<T, unknown>);
  }
  if (selectable) {
    finalColumns.push(getSelectionColumn<T>() as ColumnDef<T, unknown>);
  }
  finalColumns.push(...adaptedColumns);

  // State
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [globalFilter, setGlobalFilter] = useState('');
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [paginationState, setPaginationState] = useState<PaginationState>({
    pageIndex: 0,
    pageSize,
  });

  // Selection is controlled by the parent whenever it passes `selectedIds`,
  // and internal otherwise.
  //
  // Previously the prop was accepted and never read, so the two could disagree:
  // a parent clearing its selection — after a bulk action, or when filters
  // change — hid its own toolbar while every row stayed visibly ticked. Holding
  // one source of truth is what stops that, rather than syncing two copies.
  const isControlled = selectable && selectedIds !== undefined && !!getRowId;
  const effectiveSelection: RowSelectionState = isControlled
    ? Object.fromEntries(selectedIds.map((id) => [id, true]))
    : rowSelection;

  const handleRowSelectionChange = (
    updater: RowSelectionState | ((old: RowSelectionState) => RowSelectionState)
  ) => {
    const next = typeof updater === 'function' ? updater(effectiveSelection) : updater;
    if (!isControlled) setRowSelection(next);
    if (onSelectionChange && getRowId) {
      onSelectionChange(Object.keys(next).filter((key) => next[key]));
    }
  };

  const table = useReactTable({
    data,
    columns: finalColumns as ColumnDef<T, any>[],
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection: effectiveSelection,
      globalFilter,
      expanded,
      ...(pagination || manualPagination ? { pagination: paginationState } : {}),
    },
    enableColumnResizing: resizable,
    columnResizeMode: 'onChange',
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: handleRowSelectionChange,
    onGlobalFilterChange: setGlobalFilter,
    onExpandedChange: setExpanded,
    onPaginationChange: (updater) => {
      const newState = typeof updater === 'function' ? updater(paginationState) : updater;
      setPaginationState(newState);
      onPaginationChange?.(newState.pageIndex, newState.pageSize);
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: pagination || manualPagination ? getPaginationRowModel() : undefined,
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getExpandedRowModel: getExpandedRowModel(),
    ...(manualPagination ? { manualPagination: true, pageCount } : {}),
    ...(getRowId ? { getRowId: (row: T) => getRowId(row) } : {}),
  });

  // Loading state
  if (loading) {
    return <LoadingState variant="table" count={5} className={className} />;
  }

  // Empty state
  if (data.length === 0) {
    return (
      <div className={cn(PANEL, className)}>
        <EmptyState title={emptyTitle} description={emptyDescription} action={emptyAction} />
      </div>
    );
  }

  const showToolbar = searchable || facetedFilters?.length || columnToggle || exportable || toolbar;
  const showPagination = pagination || manualPagination;

  return (
    <div className={cn(PANEL, className)}>
      {showToolbar && (
        <DataTableToolbar
          table={table}
          toolbar={toolbar}
          searchable={searchable}
          searchPlaceholder={searchPlaceholder}
          facetedFilters={facetedFilters}
          columnToggle={columnToggle}
          exportable={exportable}
          exportFilename={exportFilename}
        />
      )}
      <DataTableContent
        table={table}
        onRowClick={onRowClick}
        rowClassName={rowClassName}
        renderExpandedRow={renderExpandedRow}
        stickyHeader={stickyHeader}
        maxHeight={maxHeight}
        caption={caption}
      />
      {showPagination && <DataTablePagination table={table} pageSizeOptions={pageSizeOptions} />}
    </div>
  );
}
